import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SUBSCRIPTION_PLANS } from '../config/stripe';
import { API_BASE_URL } from '../constants';

const API_URL = API_BASE_URL + '/api';

const SubscriptionRequired = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedPlan, setSelectedPlan] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState('');
  const [userCredentials, setUserCredentials] = useState(null);
  const [discountCode, setDiscountCode] = useState('');
  const [discountValidation, setDiscountValidation] = useState(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const [planDiscontinued, setPlanDiscontinued] = useState(false);

  // Get user credentials from sessionStorage (set during login attempt)
  useEffect(() => {
    const pendingUser = sessionStorage.getItem('pendingSubscriptionUser');
    const tempPassword = sessionStorage.getItem('tempPassword');
    setPlanDiscontinued(sessionStorage.getItem('planDiscontinued') === 'true');
    // Preselect plan chosen during registration (if available)
    const preselected = localStorage.getItem('pendingPlan');
    if (preselected && SUBSCRIPTION_PLANS[preselected]) {
      setSelectedPlan(preselected);
    }
    
    if (pendingUser && tempPassword) {
      try {
        const userData = JSON.parse(pendingUser);
        setUserCredentials({
          email: userData.email,
          password: tempPassword,
          userData: userData
        });
      } catch (e) {
        console.error('Error parsing user data:', e);
        setError('Session expired. Please log in again.');
      }
    } else {
      setError('Session expired. Please log in again.');
    }
  }, []);

  // Debug logging
  console.log('SubscriptionRequired component loaded');
  console.log('User credentials available:', !!userCredentials);
  console.log('SUBSCRIPTION_PLANS:', SUBSCRIPTION_PLANS);
  console.log('API_URL:', API_URL);

  const handlePlanSelection = (planKey) => {
    setSelectedPlan(planKey);
    setError('');
    // Clear discount validation when changing plans
    if (discountValidation) {
      validateDiscountCode();
    }
  };

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) {
      setDiscountValidation(null);
      return;
    }

    if (!selectedPlan) {
      setError('Please select a plan before applying a discount code.');
      return;
    }

    setIsValidatingDiscount(true);
    setError('');

    try {
      const plan = SUBSCRIPTION_PLANS[selectedPlan];
      const response = await fetch(`${API_URL}/validate-discount-code`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          code: discountCode.trim(),
          user_id: userCredentials?.userData?.id,
          plan_id: plan.priceId,
          amount: plan.amountCents
        })
      });

      const data = await response.json();

      if (response.ok) {
        setDiscountValidation({
          ...data,
          code: discountCode.trim()
        });
        setError('');
      } else {
        setDiscountValidation(null);
        setError(data.error || 'Invalid discount code');
      }
    } catch (error) {
      console.error('Error validating discount code:', error);
      setDiscountValidation(null);
      setError('Error validating discount code. Please try again.');
    } finally {
      setIsValidatingDiscount(false);
    }
  };

  const handleDiscountCodeChange = (e) => {
    setDiscountCode(e.target.value.toUpperCase());
    setError('');
    // Clear validation when user changes the code
    if (discountValidation) {
      setDiscountValidation(null);
    }
  };

  const handleProceedToPayment = async () => {
    if (!selectedPlan) {
      setError('Please select a subscription plan to continue.');
      return;
    }

    if (!userCredentials) {
      setError('Session expired. Please log in again.');
      return;
    }

    setIsProcessing(true);
    setError('');

    try {
      const plan = SUBSCRIPTION_PLANS[selectedPlan];

      // Paid plan: create a secure subscription session
      const checkoutResponse = await fetch(`${API_URL}/create-subscription-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: userCredentials.email,
          password: userCredentials.password,
          priceId: plan.priceId,
          discountCode: discountCode.trim()
        })
      });

      const checkoutData = await checkoutResponse.json();
      
      if (checkoutResponse.ok && checkoutData.checkout_url) {
        // Store the selected plan for later reference
        localStorage.setItem('pendingPlan', selectedPlan);
        localStorage.setItem('pendingSessionId', checkoutData.session_id);
        
        // Redirect to Stripe checkout
        window.location.href = checkoutData.checkout_url;
      } else {
        throw new Error(checkoutData.error || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment processing error:', error);
      if (error.message.includes('Invalid credentials')) {
        setError('Authentication failed. Please log in again.');
        // Clear session data and redirect to login
        sessionStorage.removeItem('pendingSubscriptionUser');
        sessionStorage.removeItem('tempPassword');
        setTimeout(() => navigate('/login'), 2000);
      } else if (error.message.includes('Email not confirmed')) {
        setError('Please check your email and click the confirmation link to activate your account.');
      } else {
        setError(error.message || 'Failed to process payment. Please try again or contact support.');
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToLogin = () => {
    // Clear session data
    sessionStorage.removeItem('pendingSubscriptionUser');
    sessionStorage.removeItem('tempPassword');
    sessionStorage.removeItem('planDiscontinued');
    navigate('/login');
  };

  const formatPrice = (cents) => {
    if (typeof cents === 'number') return `€${(cents / 100).toFixed(2)}`;
    return String(cents);
  };

  // Show loading state while checking credentials
  if (!userCredentials && !error) {
    return (
      <div className="subscription-required-page">
        <div className="subscription-container">
          <div className="subscription-header">
            <h1>Loading...</h1>
            <p>Preparing your subscription options...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="subscription-required-page">
      <div className="subscription-container">
        <div className="subscription-header">
          <h1>{t('chooseYourPlan', 'Choose Your Subscription Plan')}</h1>
          <p>{t('selectAPlanToContinue', 'Select a plan to continue using CPD Pro')}</p>
        </div>

        {planDiscontinued && (
          <div className="discontinued-notice">
            {t(
              'insightsDiscontinued',
              'The free Professional Insights plan has been discontinued. Your account and history are safe — choose a subscription below to continue.'
            )}
          </div>
        )}

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="plans-grid">
          {['professionalMonthly','professional']
            .filter((k) => SUBSCRIPTION_PLANS[k])
            .map((planKey) => {
              const plan = SUBSCRIPTION_PLANS[planKey];
              const isProfessional = planKey === 'professional';
              return (
            <div 
              key={planKey}
              className={`plan-card ${selectedPlan === planKey ? 'selected' : ''}`}
              onClick={() => handlePlanSelection(planKey)}
            >
            <div className="plan-header">
              {isProfessional && <div className="plan-badge">{t('mostPopular', 'Most Popular')}</div>}
              <h3>{plan.name}</h3>
              <div className="plan-price">
                {plan.displayPrice || (plan.amountCents === 0 ? t('free', 'Free') : formatPrice(plan.amountCents))}
              </div>
            </div>
              
              <div className="plan-features">
                {plan.features.map((feature, index) => (
                  <div key={index} className="feature-item">
                    <span className="feature-icon">✓</span>
                    <span>{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>

        {/* Discount Code Section */}
        {(selectedPlan === 'professional' || selectedPlan === 'professionalMonthly') && (
        <div className="discount-section">
          <h3>{t('haveDiscountCode', 'Have a discount code?')}</h3>
          <div className="discount-input-group">
            <input
              type="text"
              value={discountCode}
              onChange={handleDiscountCodeChange}
              placeholder={t('enterDiscountCode', 'Enter discount code')}
              className="discount-input"
              maxLength="50"
            />
            <button
              type="button"
              onClick={validateDiscountCode}
              disabled={!discountCode.trim() || !selectedPlan || isValidatingDiscount}
              className="validate-discount-btn"
            >
              {isValidatingDiscount ? t('validating', 'Validating...') : t('apply', 'Apply')}
            </button>
          </div>
          
          {discountValidation && (
            <div className="discount-success">
              <div className="discount-details">
                <span className="discount-code">✓ {discountValidation.code}</span>
                <span className="discount-amount">
                  Save {formatPrice(discountValidation.discount_amount)} 
                  ({discountValidation.discount_percentage.toFixed(1)}% off)
                </span>
              </div>
              <div className="price-breakdown">
                <div className="price-row">
                  <span>{t('originalPrice', 'Original Price:')}</span>
                  <span>{formatPrice(discountValidation.original_amount)}</span>
                </div>
                <div className="price-row discount-row">
                  <span>{t('discount', 'Discount')} ({discountValidation.discount_code.code}):</span>
                  <span>-{formatPrice(discountValidation.discount_amount)}</span>
                </div>
                <div className="price-row final-row">
                  <span>{t('finalPrice', 'Final Price:')}</span>
                  <span>{formatPrice(discountValidation.final_amount)}</span>
                </div>
              </div>
            </div>
          )}
        </div>
        )}

        <div className="subscription-actions">
          <button
            onClick={handleProceedToPayment}
            disabled={!selectedPlan || isProcessing || isValidatingDiscount}
            className="proceed-btn"
          >
            {isProcessing ? t('processing', 'Processing...') : t('proceedToPayment', 'Proceed to Payment')}
          </button>
          
          <button
            onClick={handleBackToLogin}
            className="back-btn"
          >
            {t('backToLogin', 'Back to Login')}
          </button>
        </div>
      </div>

      <style jsx>{`
        .subscription-required-page {
          min-height: 100vh;
          background: #f5f5f5;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 2rem;
        }

        .subscription-container {
          background: white;
          border-radius: 12px;
          padding: 2rem;
          max-width: 1100px;
          width: 100%;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.1);
        }

        .subscription-header {
          text-align: center;
          margin-bottom: 2rem;
        }

        .subscription-header h1 {
          color: #2c3e50;
          margin-bottom: 0.5rem;
          font-size: 2rem;
        }

        .subscription-header p {
          color: #7f8c8d;
          font-size: 1.1rem;
        }

        .error-message {
          background: #fee;
          border: 1px solid #fcc;
          color: #c33;
          padding: 1rem;
          border-radius: 6px;
          margin-bottom: 1rem;
          text-align: center;
        }

        .discontinued-notice {
          background: #eff6ff;
          border: 1px solid #bfdbfe;
          color: #1e40af;
          padding: 1rem 1.25rem;
          border-radius: 8px;
          margin-bottom: 1.5rem;
          text-align: center;
          font-size: 1rem;
          line-height: 1.5;
        }

        .plans-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(260px, 1fr));
          gap: 1.25rem;
          margin-bottom: 2rem;
          max-width: 720px;
          margin-left: auto;
          margin-right: auto;
        }

        .plan-card {
          border: 2px solid #e2e8f0;
          border-radius: 8px;
          padding: 1.5rem;
          cursor: pointer;
          transition: all 0.2s;
          background: white;
        }

        .plan-card:hover {
          border-color: #000;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .plan-card.selected {
          border-color: #000;
          background: #f8f8f8;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .plan-header {
          text-align: center;
          margin-bottom: 1.5rem;
          position: relative;
        }

        .plan-header h3 {
          color: #2c3e50;
          margin: 0 0 0.5rem 0;
          font-size: 1.5rem;
        }

        .plan-price {
          font-size: 1.6rem;
          font-weight: bold;
          color: #000;
        }

        .plan-badge {
          position: absolute;
          top: -14px;
          left: 50%;
          transform: translateX(-50%);
          background: #000;
          color: #fff;
          padding: 0.25rem 0.6rem;
          border-radius: 999px;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .plan-period {
          font-size: 1rem;
          color: #7f8c8d;
          font-weight: normal;
        }

        .plan-features {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .feature-item {
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }

        .feature-icon {
          color: #000;
          font-weight: bold;
        }

        .discount-section {
          background: #f8f9fa;
          border-radius: 8px;
          padding: 1.5rem;
          margin-bottom: 2rem;
        }

        .discount-section h3 {
          margin: 0 0 1rem 0;
          color: #2c3e50;
          font-size: 1.2rem;
        }

        .discount-input-group {
          display: flex;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .discount-input {
          flex: 1;
          padding: 0.75rem;
          border: 1px solid #d1d5db;
          border-radius: 6px;
          font-size: 1rem;
          text-transform: uppercase;
        }

        .discount-input:focus {
          outline: none;
          border-color: #000;
          box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
        }

        .validate-discount-btn {
          padding: 0.75rem 1.5rem;
          background: #000;
          color: white;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-weight: 500;
          transition: background 0.2s;
        }

        .validate-discount-btn:hover:not(:disabled) {
          background: #333;
        }

        .validate-discount-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .discount-success {
          background: #f8f9fa;
          border: 1px solid #000;
          border-radius: 6px;
          padding: 1rem;
        }

        .discount-details {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 1rem;
        }

        .discount-code {
          color: #000;
          font-weight: 600;
        }

        .discount-amount {
          color: #000;
          font-weight: 600;
        }

        .price-breakdown {
          border-top: 1px solid #e5e7eb;
          padding-top: 1rem;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          margin-bottom: 0.5rem;
        }

        .discount-row {
          color: #000;
        }

        .final-row {
          font-weight: 600;
          font-size: 1.1rem;
          border-top: 1px solid #e5e7eb;
          padding-top: 0.5rem;
          margin-top: 0.5rem;
        }

        .subscription-actions {
          display: flex;
          gap: 1rem;
          justify-content: center;
        }

        .proceed-btn {
          background: #000;
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 6px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .proceed-btn:hover:not(:disabled) {
          background: #333;
        }

        .proceed-btn:disabled {
          background: #9ca3af;
          cursor: not-allowed;
        }

        .back-btn {
          background: #6b7280;
          color: white;
          border: none;
          padding: 0.75rem 2rem;
          border-radius: 6px;
          font-size: 1.1rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.2s;
        }

        .back-btn:hover {
          background: #4b5563;
        }

        @media (max-width: 768px) {
          .subscription-container {
            padding: 1rem;
          }
          
          .plans-grid {
            grid-template-columns: 1fr;
          }
          
          .subscription-actions {
            flex-direction: column;
          }
          
          .discount-input-group {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
};

export default SubscriptionRequired; 